import { useState } from "react";
import { toast } from "sonner";
import { api, SERVICES } from "@/lib/api";
import { Loader2, ArrowRight } from "lucide-react";

const empty = {
  name: "", phone: "", email: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "",
  service: "Oil Change",
  preferred_date: "", preferred_time: "",
  notes: "",
};

const BookingForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/appointments", payload);
      toast.success("Booking received! We'll text you a confirmation shortly.");
      setSubmitted(true);
      setForm(empty);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please call us at (405) 741-1155.";
      toast.error(typeof msg === "string" ? msg : "Validation error — please check your fields.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0a0a0a] border border-[#2e2e2e] rounded-none text-white px-4 py-3 font-['Exo_2'] focus:border-[#e10600] focus:outline-none focus:ring-1 focus:ring-[#e10600] transition-colors placeholder:text-gray-600";
  const labelCls = "block font-['Exo_2'] text-[10px] uppercase tracking-[0.25em] text-gray-400 font-bold mb-2";

  return (
    <section id="booking" data-testid="booking-section" className="bg-black py-24 lg:py-32 border-t border-[#2e2e2e]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-[2px] w-12 bg-[#e10600]" />
            <span className="font-['Exo_2'] uppercase text-xs tracking-[0.4em] text-[#e10600] font-bold">Book Now</span>
          </div>
          <h2 className="font-['Anton'] text-white text-5xl md:text-6xl uppercase leading-[0.95] mb-6">
            Schedule<br /><span className="text-[#e10600]">your service.</span>
          </h2>
          <p className="font-['Exo_2'] text-gray-400 text-base leading-relaxed mb-8">
            Pick a date and service. We'll text a confirmation to your phone after we review.
          </p>
          <div className="border border-[#2e2e2e] p-6 bg-[#0f0f0f]">
            <div className="font-['Exo_2'] text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-2">Need it sooner?</div>
            <a href="tel:+14057411155" className="font-['Anton'] text-3xl text-[#e10600] hover:text-[#ff1a1a]">(405) 741-1155</a>
            <div className="font-['Exo_2'] text-gray-500 text-xs mt-2">Mon – Fri  10 AM – 6 PM</div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="booking-form" className="lg:col-span-8 bg-[#0f0f0f] border border-[#2e2e2e] p-6 md:p-10 space-y-6">
          {submitted && (
            <div data-testid="booking-success-banner" className="bg-[#e10600]/10 border border-[#e10600] text-white p-4 font-['Exo_2'] text-sm">
              Thanks! Your booking is in our queue. Submit another?
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Full Name *</label>
              <input data-testid="booking-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="John Doe" />
            </div>
            <div>
              <label className={labelCls}>Phone *</label>
              <input data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(405) 555-1234" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email (optional)</label>
            <input data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className={labelCls}>Year *</label>
              <input data-testid="booking-year-input" required value={form.vehicle_year} onChange={set("vehicle_year")} className={inputCls} placeholder="2018" />
            </div>
            <div>
              <label className={labelCls}>Make *</label>
              <input data-testid="booking-make-input" required value={form.vehicle_make} onChange={set("vehicle_make")} className={inputCls} placeholder="Ford" />
            </div>
            <div>
              <label className={labelCls}>Model *</label>
              <input data-testid="booking-model-input" required value={form.vehicle_model} onChange={set("vehicle_model")} className={inputCls} placeholder="F-150" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className={labelCls}>Service *</label>
              <select data-testid="booking-service-select" required value={form.service} onChange={set("service")} className={inputCls + " appearance-none cursor-pointer"}>
                {SERVICES.map((s) => <option key={s.key} value={s.key}>{s.key}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Date *</label>
              <input data-testid="booking-date-input" required type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Time *</label>
              <input data-testid="booking-time-input" required type="time" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes (optional)</label>
            <textarea data-testid="booking-notes-input" rows={4} value={form.notes} onChange={set("notes")} className={inputCls + " resize-none"} placeholder="Describe the issue or any special requests…" />
          </div>

          <button type="submit" data-testid="booking-submit-button" disabled={loading}
            className="group inline-flex items-center gap-3 bg-[#e10600] hover:bg-[#ff1a1a] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 uppercase tracking-widest text-sm font-bold transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
            {loading ? "Sending…" : "Submit Booking"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default BookingForm;
